/**
 * LeaveEncashmentPage - HR Module for Leave Encashment Management
 * - View employees eligible for EL encashment
 * - Process encashment requests
 * - Track encashment history and payments
 */
import { useState, useMemo } from 'react';
import { leaveAPI, employeeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApi, useMutation } from '../hooks/useApi';
import { formatDate, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Loader2, DollarSign, Users, CheckCircle, Clock, AlertCircle, 
  Wallet, FileText, ArrowRight, Search, RefreshCw
} from 'lucide-react';

// Status configurations
const ENCASHMENT_STATUS = {
  pending: { label: 'Pending Approval', variant: 'secondary', icon: Clock },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle },
  paid: { label: 'Paid', variant: 'success', icon: DollarSign },
  rejected: { label: 'Rejected', variant: 'destructive', icon: AlertCircle },
};

export default function LeaveEncashmentPage() {
  const { user, isHR, isAdmin, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('eligible');
  const [searchTerm, setSearchTerm] = useState('');
  const [processDialog, setProcessDialog] = useState({ open: false, employee: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, encashment: null });
  const [encashDays, setEncashDays] = useState('');
  const [encashNotes, setEncashNotes] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  // Check authorization
  const canManageEncashments = isHR || isAdmin || isSuperAdmin;

  // Fetch employees with leave balances
  const { data: employees, loading: loadingEmployees, refresh: refreshEmployees } = useApi(
    employeeAPI.getAll,
    { immediate: canManageEncashments }
  );

  // Fetch encashment history
  const { data: encashments, loading: loadingEncashments, refresh: refreshEncashments } = useApi(
    () => leaveAPI.getEncashments({}),
    { immediate: canManageEncashments }
  );

  // Process encashment mutation
  const { mutate: processEncashment, loading: processing } = useMutation(
    ({ employeeId, days, notes }) => leaveAPI.encashEL(employeeId, days, notes),
    {
      successMessage: 'Encashment processed successfully',
      onSuccess: () => {
        setProcessDialog({ open: false, employee: null });
        setEncashDays('');
        setEncashNotes('');
        refreshEncashments();
        refreshEmployees();
      }
    }
  );

  // Approve encashment mutation
  const { mutate: approveEncashment, loading: approving } = useMutation(
    (id) => leaveAPI.approveEncashment(id),
    {
      successMessage: 'Encashment approved',
      onSuccess: () => refreshEncashments()
    }
  );

  // Mark paid mutation
  const { mutate: markPaid, loading: markingPaid } = useMutation(
    ({ id, reference }) => leaveAPI.markEncashmentPaid(id, reference),
    {
      successMessage: 'Encashment marked as paid',
      onSuccess: () => {
        setPaymentDialog({ open: false, encashment: null });
        setPaymentReference('');
        refreshEncashments();
      }
    }
  );

  // Eligible employees for encashment (those with EL > 12)
  const eligibleEmployees = useMemo(() => {
    if (!employees) return [];
    return employees.filter(emp => {
      const elBalance = emp.leave_balance?.EL || 0;
      return elBalance > 12 && emp.employment_status === 'confirmed';
    }).map(emp => {
      const basicSalary = emp.basic_salary || 0;
      const perDayRate = basicSalary / 30;
      const excessEL = (emp.leave_balance?.EL || 0) - 12;
      return {
        ...emp,
        excessEL: excessEL,
        totalEL: emp.leave_balance?.EL || 0,
        perDayRate: perDayRate,
        maxEncashmentAmount: perDayRate * excessEL
      };
    });
  }, [employees]);

  // Filter eligible employees by search
  const filteredEligible = useMemo(() => {
    if (!searchTerm) return eligibleEmployees;
    const term = searchTerm.toLowerCase();
    return eligibleEmployees.filter(emp => 
      emp.name?.toLowerCase().includes(term) ||
      emp.employee_id?.toLowerCase().includes(term) ||
      emp.department?.toLowerCase().includes(term)
    );
  }, [eligibleEmployees, searchTerm]);

  // Encashment statistics
  const stats = useMemo(() => {
    const allEncashments = encashments || [];
    return {
      pending: allEncashments.filter(e => e.status === 'pending').length,
      approved: allEncashments.filter(e => e.status === 'approved').length,
      paid: allEncashments.filter(e => e.status === 'paid').length,
      totalAmount: allEncashments
        .filter(e => e.status === 'paid')
        .reduce((sum, e) => sum + (e.amount || 0), 0)
    };
  }, [encashments]);

  // Handle encashment process
  const handleProcessEncashment = async () => {
    const days = parseFloat(encashDays);
    if (!days || days <= 0) {
      toast.error('Please enter valid number of days');
      return;
    }
    if (days > processDialog.employee?.excessEL) {
      toast.error(`Cannot encash more than ${processDialog.employee?.excessEL} excess days`);
      return;
    }
    await processEncashment({
      employeeId: processDialog.employee.id,
      days,
      notes: encashNotes
    });
  };

  // Handle mark paid
  const handleMarkPaid = async () => {
    if (!paymentReference.trim()) {
      toast.error('Please enter payment reference');
      return;
    }
    await markPaid({
      id: paymentDialog.encashment.id,
      reference: paymentReference
    });
  };

  if (!canManageEncashments) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">Only HR, Admin, or Super Admin can access this page.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="leave-encashment-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Leave Encashment Management</h1>
          <p className="text-muted-foreground">Process and track EL encashments for employees</p>
        </div>
        <Button variant="outline" onClick={() => { refreshEmployees(); refreshEncashments(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Eligible Employees</p>
                <p className="text-2xl font-bold">{eligibleEmployees.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Payment</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
              <Wallet className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalAmount)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="eligible" data-testid="tab-eligible">
            <Users className="h-4 w-4 mr-2" />
            Eligible Employees
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Clock className="h-4 w-4 mr-2" />
            Pending ({stats.pending + stats.approved})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Eligible Employees Tab */}
        <TabsContent value="eligible" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Employees Eligible for Encashment</CardTitle>
                  <CardDescription>Employees with more than 12 EL days (max carry forward)</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-employees"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEmployees ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredEligible.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No employees eligible for encashment</p>
                  <p className="text-sm">Employees must have more than 12 EL days to be eligible</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-right">Total EL</TableHead>
                      <TableHead className="text-right">Excess EL</TableHead>
                      <TableHead className="text-right">Est. Amount</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEligible.map((emp) => (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.employee_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{emp.name}</p>
                            <p className="text-sm text-muted-foreground">{emp.employee_id}</p>
                          </div>
                        </TableCell>
                        <TableCell>{emp.department || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{emp.totalEL}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{emp.excessEL} days</Badge>
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {formatCurrency(emp.maxEncashmentAmount || 0)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => setProcessDialog({ open: true, employee: emp })}
                            data-testid={`process-btn-${emp.employee_id}`}
                          >
                            Process
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Encashments</CardTitle>
              <CardDescription>Encashments awaiting approval or payment</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingEncashments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(encashments || [])
                      .filter(e => e.status === 'pending' || e.status === 'approved')
                      .map((enc) => {
                        const statusConfig = ENCASHMENT_STATUS[enc.status] || ENCASHMENT_STATUS.pending;
                        return (
                          <TableRow key={enc.id} data-testid={`encashment-row-${enc.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{enc.employee_name}</p>
                                <p className="text-sm text-muted-foreground">{enc.employee_id}</p>
                              </div>
                            </TableCell>
                            <TableCell>{enc.days}</TableCell>
                            <TableCell>{formatCurrency(enc.amount || 0)}</TableCell>
                            <TableCell>
                              <Badge variant={statusConfig.variant}>
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(enc.created_at)}</TableCell>
                            <TableCell className="text-right space-x-2">
                              {enc.status === 'pending' && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => approveEncashment(enc.id)}
                                  disabled={approving}
                                  data-testid={`approve-btn-${enc.id}`}
                                >
                                  {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve'}
                                </Button>
                              )}
                              {enc.status === 'approved' && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => setPaymentDialog({ open: true, encashment: enc })}
                                  data-testid={`pay-btn-${enc.id}`}
                                >
                                  Mark Paid
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {(encashments || []).filter(e => e.status === 'pending' || e.status === 'approved').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No pending encashments
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Encashment History</CardTitle>
              <CardDescription>All processed encashment records</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingEncashments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment Ref</TableHead>
                      <TableHead>Processed By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(encashments || []).map((enc) => {
                      const statusConfig = ENCASHMENT_STATUS[enc.status] || ENCASHMENT_STATUS.pending;
                      return (
                        <TableRow key={enc.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{enc.employee_name}</p>
                              <p className="text-sm text-muted-foreground">{enc.employee_id}</p>
                            </div>
                          </TableCell>
                          <TableCell>{enc.days}</TableCell>
                          <TableCell>{formatCurrency(enc.amount || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={statusConfig.variant}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{enc.payment_reference || '-'}</TableCell>
                          <TableCell>{enc.processed_by_name || '-'}</TableCell>
                          <TableCell>{formatDate(enc.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {(encashments || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No encashment records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Process Encashment Dialog */}
      <Dialog open={processDialog.open} onOpenChange={(open) => setProcessDialog({ open, employee: open ? processDialog.employee : null })}>
        <DialogContent data-testid="process-encashment-dialog">
          <DialogHeader>
            <DialogTitle>Process Leave Encashment</DialogTitle>
            <DialogDescription>
              Encash excess EL days for {processDialog.employee?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Total EL Balance</p>
                <p className="text-lg font-semibold">{processDialog.employee?.totalEL} days</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Excess EL (Encashable)</p>
                <p className="text-lg font-semibold text-green-600">{processDialog.employee?.excessEL} days</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Per Day Rate</p>
                <p className="text-lg font-semibold">{formatCurrency(processDialog.employee?.perDayRate || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Max Encashment Amount</p>
                <p className="text-lg font-semibold text-blue-600">{formatCurrency(processDialog.employee?.maxEncashmentAmount || 0)}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="encashDays">Days to Encash</Label>
              <Input
                id="encashDays"
                type="number"
                min="0.5"
                step="0.5"
                max={processDialog.employee?.excessEL || 0}
                value={encashDays}
                onChange={(e) => setEncashDays(e.target.value)}
                placeholder={`Max: ${processDialog.employee?.excessEL || 0} days`}
                data-testid="encash-days-input"
              />
              {encashDays && processDialog.employee?.perDayRate > 0 && (
                <p className="text-sm text-muted-foreground">
                  Estimated Amount: <span className="font-semibold text-green-600">
                    {formatCurrency(parseFloat(encashDays) * processDialog.employee.perDayRate)}
                  </span>
                </p>
              )}
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Approved encashments will be automatically added to the <strong>December {new Date().getFullYear()} payslip</strong> as "EL Encashment".
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="encashNotes">Notes (Optional)</Label>
              <Textarea
                id="encashNotes"
                value={encashNotes}
                onChange={(e) => setEncashNotes(e.target.value)}
                placeholder="Add any relevant notes..."
                rows={3}
                data-testid="encash-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialog({ open: false, employee: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessEncashment} 
              disabled={processing || !encashDays}
              data-testid="confirm-encash-btn"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Process Encashment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => setPaymentDialog({ open, encashment: open ? paymentDialog.encashment : null })}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark Encashment as Paid</DialogTitle>
            <DialogDescription>
              Record payment for {paymentDialog.encashment?.employee_name}'s encashment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Days Encashed</p>
                <p className="text-lg font-semibold">{paymentDialog.encashment?.days} days</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="text-lg font-semibold">{formatCurrency(paymentDialog.encashment?.amount || 0)}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="paymentRef">Payment Reference *</Label>
              <Input
                id="paymentRef"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Transaction ID / Cheque No. / Reference"
                data-testid="payment-ref-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog({ open: false, encashment: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkPaid} 
              disabled={markingPaid || !paymentReference.trim()}
              data-testid="confirm-paid-btn"
            >
              {markingPaid ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
